import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import replace from 'lodash/replace';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import classNames from 'classnames';
import Waypoint from 'react-waypoint';
import loadable from '@loadable/component';

import { translationPropType } from '@eb/i18n-ebspa';
import {
    GRID_TYPE,
    LIST_TYPE,
} from 'eventbrite_design_system/mediaCardContent/constants';

import { EVENT_CARD_SHARE_OPTIONS_PROPTYPE } from 'eventbrite_design_system/eventCard/constants';

import {
    collections as collectionsDuck,
    getCanonicalSearchUrl,
    withEventSaveNotification,
} from '@eb/destination-utils';

import SeeMore from './SeeMore';
import EmptyState from './EmptyState';
import {
    BUCKET_CONFIG_PROPTYPE,
    EVENTS_PROPTYPES,
    SEE_MORE_PROPTYPE,
    EVENT_SEARCH_PROPTYPES,
} from '../constants/propTypes';
import {
    BEST_OF_CITY_EVENTS,
    FOLLOWED_PROFILES,
    POPULAR_EVENTS,
    LIKED_EVENTS,
} from '../constants/constants';
import {
    FREE_TAB,
    FOR_YOU,
    ALL_TAB,
    SEASONAL_TAB,
} from '../constants/tabConfig';
import { BASE_SEASONAL_CONTENT_PROPTYPES } from '../constants/seasonal';
import { getBucketConfig, getEventsLayout } from '../utils/feed';

import './EventsBucket.scss';
import './EventsBucketPremium.scss';

const ConsumerCards = loadable(() => import('./ConsumerCards'));

const {
    constants: { COLLECTION_PROP_TYPE },
} = collectionsDuck;

const DESKTOP_DEFAULT_LAYOUT_CONFIG = {
    max: 16,
    bucketClassNames: 'feed-events-bucket__content',
    layout: {
        default: {
            containerClasses: [
                'eds-g-cell eds-g-cell--has-overflow eds-g-cell-6-12 eds-g-cell-mn-4-12 eds-g-cell-lg-3-12 feed__card-cell',
            ],
        },
    },
};

const MOBILE_DEFAULT_LAYOUT_CONFIG = {
    max: 16,
    bucketClassNames: 'feed-events-bucket__content',
    layout: {
        default: {
            containerClasses: [
                'eds-g-cell eds-g-cell--has-overflow eds-g-cell-1-1 feed__card-cell',
            ],
        },
    },
    propsOverwrite: {
        default: {
            type: LIST_TYPE,
        },
        1: {
            type: GRID_TYPE,
        },
        9: {
            type: GRID_TYPE,
        },
    },
};

const LAYOUT_WIDTH_MAP = {
    odd: 792,
    even: 1272,
};

const _isInOddLayout = (windowWidth) =>
    windowWidth >= LAYOUT_WIDTH_MAP.odd && windowWidth < LAYOUT_WIDTH_MAP.even;

class EventsBucket extends PureComponent {
    static propTypes = {
        onSaveEvent: PropTypes.func.isRequired,
        onSeeMore: PropTypes.func.isRequired,
        onViewEvent: PropTypes.func.isRequired,

        onAddOrRemoveEventToCollection: PropTypes.func,
        onCreateCollection: PropTypes.func,

        bucketConfig: BUCKET_CONFIG_PROPTYPE,
        userCollections: COLLECTION_PROP_TYPE,
        currentPlace: PropTypes.string,
        bucketKey: PropTypes.string,
        events: EVENTS_PROPTYPES,
        isAuthenticated: PropTypes.bool,
        isMobile: PropTypes.bool,
        name: translationPropType,
        seeMore: SEE_MORE_PROPTYPE,
        subtitle: translationPropType,
        shouldUseWaypoints: PropTypes.bool,
        shouldShowSeeMore: PropTypes.bool,
        searchCriteria: EVENT_SEARCH_PROPTYPES,
        locationSlug: PropTypes.string,
        shouldShowOnboardingEntryPoint: PropTypes.bool,
        tabKey: PropTypes.string,
        userId: PropTypes.string,
        shareOptions: EVENT_CARD_SHARE_OPTIONS_PROPTYPE,
        onWillShareClick: PropTypes.func,
        onClickOnBoardingCard: PropTypes.func,
        seasonalContent: PropTypes.shape(BASE_SEASONAL_CONTENT_PROPTYPES),
    };

    static defaultProps = {
        events: [],
        searchCriteria: {},
    };

    constructor(props) {
        super(props);

        this.state = {
            visible: false,
        };
    }

    componentDidMount() {
        this.scrollListener = window.addEventListener(
            'resize',
            this._updateDimensions,
        );

        this.setState({
            visible: !this.props.isMobile && !this.props.shouldUseWaypoints,
            windowWidth: window.innerWidth,
        });
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this._updateDimensions);
    }

    _updateDimensions = () => {
        this.setState({
            windowWidth: window.innerWidth,
        });
    };

    _handleWaypointEnter = () => {
        this.setState({ visible: true });
    };

    _handleOnSaveEvent = (eventId, shouldSave) => {
        const { onSaveEvent } = this.props;

        return onSaveEvent(eventId, shouldSave);
    };

    _buildSeeMore = () => {
        const {
            bucketKey,
            seeMore,
            onSeeMore,
            searchCriteria,
            shouldShowSeeMore,
            locationSlug,
            tabKey,
            userId,
            seasonalContent,
        } = this.props;

        let seeMoreUrl = seeMore;

        if (shouldShowSeeMore) {
            if (isEmpty(seeMore) && searchCriteria && !isEmpty(locationSlug)) {
                let q = '';

                if (tabKey === SEASONAL_TAB) {
                    q = get(
                        seasonalContent,
                        'byDate.eventSearchQ',
                        get(seasonalContent, 'byDate.tabName', ''),
                    );
                    if (!isString(q)) {
                        q = q.toString();
                    }
                }

                const { price, tags, dates } = searchCriteria;

                seeMoreUrl = {
                    url: getCanonicalSearchUrl({
                        slug: locationSlug,
                        price,
                        tags,
                        dates,
                        q,
                    }),
                };
            }

            if (bucketKey === LIKED_EVENTS && tabKey === FOR_YOU) {
                seeMoreUrl = {
                    url: `/u/${userId}/favorites/events`,
                };
            }

            if (bucketKey === FOLLOWED_PROFILES && tabKey === FOR_YOU) {
                seeMoreUrl = {
                    url: `/u/${userId}/following`,
                };
            }
        }

        /* This code is commented in order to always show the "see more" button as it is the only CTA that we have for the buckets */

        // const isDynamicSeeMore = bucketConfig && bucketConfig.isDynamicSeeMore && bucketLayoutConfig.max < size(events);
        // const isStaticSeeMore = (!bucketConfig || (bucketConfig && !bucketConfig.isDynamicSeeMore)) && bucketLayoutConfig.max <= size(events);

        // // Hide SeeMore button if there are less events than max
        // if (isDynamicSeeMore || isStaticSeeMore) {
        //     let seeMoreUrl = seeMore;

        //     if (isEmpty(seeMore) && searchCriteria && !isEmpty(locationSlug)) {
        //         const {
        //             price,
        //             tags,
        //             dates,
        //         } = searchCriteria;

        //         seeMoreUrl = {url: getCanonicalSearchUrl({
        //             slug: locationSlug,
        //             price,
        //             tags,
        //             dates,
        //         })};
        //     }
        //     seeMoreComponent = (
        //         <SeeMore
        //             bucketKey={bucketKey}
        //             onClick={onSeeMore}
        //             seeMore={seeMoreUrl}
        //         />
        //     );
        // }

        return (
            <SeeMore
                bucketKey={bucketKey}
                onClick={onSeeMore}
                seeMore={seeMoreUrl}
            />
        );
    };

    render() {
        const {
            // eslint-disable-next-line no-shadow
            name,
            subtitle,
            events,
            bucketConfig,
            bucketKey,
            currentPlace,
            onViewEvent,
            isAuthenticated,
            onAddOrRemoveEventToCollection,
            onCreateCollection,
            userCollections,
            searchCriteria: { price },
            shouldShowOnboardingEntryPoint,
            shouldUseWaypoints,
            shareOptions,
            onWillShareClick,
            onClickOnBoardingCard,
            tabKey,
        } = this.props;
        const { visible, windowWidth } = this.state;
        // TODO: move this to feedConfig
        const isSpecialBucket = [BEST_OF_CITY_EVENTS, POPULAR_EVENTS].includes(
            bucketKey,
        );
        const isFreeTab = price === FREE_TAB;

        let titleComponent = null;

        let subtitleComponent = null;

        if (isEmpty(events)) {
            return (
                <EmptyState
                    bucketKey={bucketKey}
                    emptyState={bucketConfig.emptyState}
                    isAuthenticated={isAuthenticated}
                />
            );
        }

        if (name) {
            const titleClassNames = classNames(
                'eds-text-color--grey-900',
                'eds-text-hs',
            );
            const fixedName = replace(name, '<location>', currentPlace);

            titleComponent = (
                <span className={titleClassNames}>{fixedName}</span>
            );
        }

        if (subtitle) {
            const fixedSubtitle = replace(subtitle, '<location>', currentPlace);

            subtitleComponent = (
                <span className="eds-text-bm eds-text-color--grey-600">
                    {fixedSubtitle}
                </span>
            );
        }

        const desktopLayoutConfig = getBucketConfig({
            bucketConfig: get(
                bucketConfig,
                'overwrite.props.layout.desktop.events',
                {},
            ),
            defaultConfig: DESKTOP_DEFAULT_LAYOUT_CONFIG,
        });
        const desktopEventsGroups = getEventsLayout({
            events,
            bucketConfig: desktopLayoutConfig,
        });

        const mobileLayoutConfig = getBucketConfig({
            bucketConfig: get(
                bucketConfig,
                'overwrite.props.layout.mobile.events',
                {},
            ),
            defaultConfig: MOBILE_DEFAULT_LAYOUT_CONFIG,
        });
        const mobileEventsGroups = getEventsLayout({
            events,
            bucketConfig: mobileLayoutConfig,
        });

        const bucketClassNamesDesktop = classNames(
            'feed-events-bucket',
            'feed-events-bucket--animated',
            `feed-events--${bucketKey}`,
            {
                animate: visible,
            },
        );
        const bucketClassNamesMobile = classNames(
            bucketClassNamesDesktop,
            'feed-events-bucket--mobile',
        );
        const bucketHeaderClassNames = classNames(
            'eds-l-pad-bot-6',
            {
                'eds-l-sm-pad-bot-10 eds-l-sw-pad-bot-8 pad-bot-mn-up': !isSpecialBucket,
                'pad-bot-sm-up--premium': isSpecialBucket,
            },
            'eds-l-mn-pad-hor-3',
            'eds-l-md-pad-hor-3',
            'eds-l-mw-pad-hor-3',
            'eds-l-ln-pad-hor-3',
            'eds-l-lg-pad-hor-3',
            'eds-l-lw-pad-hor-3',
        );

        let bucketHeader = null;

        if (subtitleComponent || titleComponent) {
            bucketHeader = (
                <div className={bucketHeaderClassNames}>
                    {titleComponent}
                    <div>{subtitleComponent}</div>
                </div>
            );
        }

        const consumerCardsProps = {
            bucketKey,
            isAuthenticated,
            onViewEvent,
            onSaveEvent: this._handleOnSaveEvent,
            onAddOrRemoveEventToCollection,
            onCreateCollection,
            userCollections,
            isFreeTab,
            shouldShowOnboardingEntryPoint,
            isFirstBucket: !shouldUseWaypoints && tabKey === ALL_TAB,
            isInOddLayout: _isInOddLayout(windowWidth),
            shareOptions,
            onWillShareClick,
            onClickOnBoardingCard,
            shouldShowOnboardingCard: get(
                bucketConfig,
                'shouldShowOnboardingCard',
                false,
            ),
        };

        return (
            <div>
                <div className="eds-show-up-sw">
                    <Waypoint
                        onEnter={this._handleWaypointEnter}
                        topOffset="-500px"
                    >
                        <div className={bucketClassNamesDesktop}>
                            {bucketHeader}

                            <ConsumerCards
                                {...consumerCardsProps}
                                bucketLayoutConfig={desktopLayoutConfig}
                                eventsGroups={desktopEventsGroups}
                                cardType="grid"
                            />

                            {this._buildSeeMore(desktopLayoutConfig)}
                        </div>
                    </Waypoint>
                </div>

                <div className="eds-show-down-sm">
                    <Waypoint
                        onEnter={this._handleWaypointEnter}
                        topOffset="-300px"
                    >
                        <div className={bucketClassNamesMobile}>
                            {bucketHeader}

                            <ConsumerCards
                                {...consumerCardsProps}
                                bucketLayoutConfig={mobileLayoutConfig}
                                eventsGroups={mobileEventsGroups}
                                cardType="list"
                            />

                            {this._buildSeeMore(mobileLayoutConfig)}
                        </div>
                    </Waypoint>
                </div>
            </div>
        );
    }
}

export default withEventSaveNotification(EventsBucket);
